import type { VariantInfo } from '~/modules/campaign';

import { BlockStack, Box, Checkbox, Icon, InlineStack, Text } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import { GVariantBadge } from '~/presentation/ui';
import { cls, parseTemplateType } from '~/utils';
import { useToggle } from '~/hooks/useToggle';
import { useMemo } from 'react';

interface CampaignSelectorProps {
  variants: VariantInfo[];
  id: string;
  isSelected: boolean;
  isSelectMultiple?: boolean;
  onChange: (value: string) => void;
}

export const CampaignSelector: React.FC<CampaignSelectorProps> = ({
  variants,
  id,
  isSelected,
  onChange,
  isSelectMultiple,
}) => {
  const { setFalse, setTrue, value: isHover } = useToggle(false);

  const templateType = useMemo(() => {
    return parseTemplateType(variants[0].themePageInfo?.templateInfo?.type);
  }, [variants]);

  const backgroundColor = useMemo(() => {
    if (!isSelectMultiple && isSelected) return 'bg-surface-active';
    if (isHover) return 'bg-surface-hover';

    return 'bg-surface';
  }, [isHover, isSelected, isSelectMultiple]);

  return (
    <div
      onMouseEnter={setTrue}
      onMouseLeave={setFalse}
      className={cls(
        'cursor-pointer h-[68px] p-3 rounded-lg border-[1px] border-solid',
        backgroundColor,
        {
          'border-border': !isSelected,
          'border-inverse': isSelected,
        },
      )}
      onClick={() => onChange(id)}
    >
      <InlineStack blockAlign="center" gap="300" wrap={false}>
        <Box minWidth="0">
          <BlockStack gap="100">
            {variants.map((variant) => {
              return (
                <GVariantBadge
                  key={variant.id}
                  size="xs"
                  {...variant.badge}
                  labelProps={{
                    variant: 'bodyMd',
                    fontWeight: 'medium',
                  }}
                  label={variant.themePageInfo?.label}
                />
              );
            })}
          </BlockStack>
        </Box>
        <div className="flex flex-1 items-center justify-end gap-3 min-w-fit">
          <Text variant="bodySm" as="p" tone="subdued">
            {templateType}
          </Text>
          {isSelectMultiple && <Checkbox label="" checked={isSelected} />}
          {!isSelectMultiple && isSelected && (
            <Box>
              <Icon source={CheckIcon} />
            </Box>
          )}
        </div>
      </InlineStack>
    </div>
  );
};
